import React, { useCallback, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "../Buttons/Button";
import {
  DeleteIcon,
  EditIcon,
  PrintIcon,
  ShareIcon,
} from "../../../assets/icons/iconsProvider";
import { buttonNameConstants, buttonTypeConstants } from "../../../constants";
import AgGridPaginationComponent from "./AgGridPaginationComponent";
import AGGridToolTip from "../toolTipComponent/AGGridToolTip";
import CustomGridHeader from "./CustomGridHeader";
import { isTransferInventoryEnabled } from "../../inventoryUtility";

const ServerPaginationTable = ({
  colDefs,
  gridRef,
  gridData,
  gridState,
  getRowStyle,
  tableHeight,
  selectedRows,
  onCellClicked,
  updateGridState,
  components = null,
  rowClassRules = {},
  defaultColDef = null,
  pagination = true,
  onSelectionChanged,
  animateRows = true,
  getRowId = () => {},
  editType = "fullRow",
  tooltipShowDelay = 0,
  handleDeleteAllClick,
  handlePrintLabelClick,
  isDataLoading = false,
  singleClickEdit = true,
  handleTransferInventory,
  tooltipHideDelay = 1000,
  tooltipInteraction = true,
  suppressCellFocus = true,
  rowSelection = "multiple",
  isDisablePaginationActions,
  alwaysShowVerticalScroll = false,
  suppressRowClickSelection = true,
  suppressRowHoverHighlight = true,
  alwaysShowHorizontalScroll = false,
  suppressDragLeaveHidesColumns = true,
  defaultGridSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  currentStoreCompany = {},
  stores = [],
  scrollRef = { current: 0 },
  isInlineEditRefresh = false,
  setIsInlineEditRefresh,
  inlineEditRefreshScrollValue = 0,
}) => {
  var fromEdit = false;
  var fromEditScrollValue = 0;
  const onFirstDataRendered = useCallback((params) => {
    params.api.sizeColumnsToFit();

    params.api.gridBodyCtrl.eBodyViewport.scrollTop = scrollRef.current;

    fromEdit = true;
    fromEditScrollValue = scrollRef.current;
  }, []);
  const onModelUpdated = useCallback(
    (params) => {
      if (fromEdit) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          fromEditScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop = fromEditScrollValue;
          fromEdit = false;
          fromEditScrollValue = 0;
        }
      } else if (isInlineEditRefresh) {
        if (
          params.api.gridBodyCtrl.eBodyViewport.scrollHeight -
            params.api.gridBodyCtrl.eBodyViewport.clientHeight >
          inlineEditRefreshScrollValue
        ) {
          params.api.ensureIndexVisible(gridState.pageSize, "middle");
          params.api.gridBodyCtrl.eBodyViewport.scrollTop =
            inlineEditRefreshScrollValue;
          setIsInlineEditRefresh?.(false);
        }
      }
    },
    [isInlineEditRefresh]
  );
  const onSortChanged = () => {
    scrollRef.current = 0;
    gridRef.current.api.gridBodyCtrl.eBodyViewport.scrollTop = 0;
  };

  const agGridDefaultColDef = useMemo(() => {
    return {
      cellStyle: { display: "flex", alignItems: "center" },
      resizable: false,
      tooltipComponent: AGGridToolTip,
      headerComponentParams: {
        gridState: gridState,
        updateGridState: updateGridState,
      },
    };
  }, [gridState]);

  const agGridComponents = useMemo(() => {
    return {
      agColumnHeader: CustomGridHeader,
    };
  }, []);

  const onGridSizeChanged = useCallback(
    (params) => {
      var gridWidth = document.querySelector(".ag-body-viewport").clientWidth;
      var columnsToShow = [];
      var columnsToHide = [];
      var totalColsWidth = 0;
      var allColumns = params.api.getColumns();
      if (allColumns && allColumns.length > 0) {
        for (var i = 0; i < allColumns.length; i++) {
          var column = allColumns[i];
          totalColsWidth += column.getMinWidth() || 0;
          if (totalColsWidth > gridWidth) {
            columnsToHide.push(column.getColId());
          } else {
            columnsToShow.push(column.getColId());
          }
        }
      }
      params.api.setColumnsVisible(columnsToShow, true);
      params.api.setColumnsVisible(columnsToHide, false);
      window.setTimeout(() => {
        params.api.sizeColumnsToFit();
      }, 10);
    },
    [window]
  );

  return (
    <div
      className="ag-theme-quartz" // applying the grid theme
      style={{
        height: tableHeight,
        paddingBottom: "5px",
      }} // the grid will fill the size of the parent container
    >
      {selectedRows?.length > 0 && (
        <div
          className={`d-flex justify-content-end gap-2 ${
            handlePrintLabelClick ? "py-2" : ""
          } `}
        >
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.DELETE}
              buttonType={buttonTypeConstants.RED_BUTTON}
              IconImage={DeleteIcon}
              type="button"
              handleClick={() => handleDeleteAllClick()}
            />
          )}
          {isTransferInventoryEnabled(currentStoreCompany, stores) && (
            <Button
              label={buttonNameConstants.TRANSFER}
              buttonType={buttonTypeConstants.GHOST_BUTTON}
              IconImage={ShareIcon}
              type="button"
              handleClick={() => handleTransferInventory()}
            />
          )}
          {handlePrintLabelClick && (
            <Button
              label={buttonNameConstants.PRINT_LABEL}
              type="button"
              IconImage={PrintIcon}
              buttonType={buttonTypeConstants.LIGHT_PURPLE}
              handleClick={() => handlePrintLabelClick()}
            />
          )}
        </div>
      )}
      <AgGridReact
        ref={gridRef}
        rowData={gridData}
        getRowId={getRowId}
        editType={editType}
        columnDefs={colDefs}
        animateRows={animateRows}
        getRowStyle={getRowStyle}
        rowSelection={rowSelection}
        rowClassRules={rowClassRules}
        onCellClicked={onCellClicked}
        singleClickEdit={singleClickEdit}
        tooltipShowDelay={tooltipShowDelay}
        tooltipHideDelay={tooltipHideDelay}
        suppressCellFocus={suppressCellFocus}
        onGridSizeChanged={onGridSizeChanged}
        onSelectionChanged={onSelectionChanged}
        tooltipInteraction={tooltipInteraction}
        onFirstDataRendered={onFirstDataRendered}
        autoSizeStrategy={defaultGridSizeStrategy}
        components={components || agGridComponents}
        alwaysShowVerticalScroll={alwaysShowVerticalScroll}
        defaultColDef={defaultColDef || agGridDefaultColDef}
        suppressRowClickSelection={suppressRowClickSelection}
        suppressRowHoverHighlight={suppressRowHoverHighlight}
        alwaysShowHorizontalScroll={alwaysShowHorizontalScroll}
        suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
        onBodyScroll={(event) => {
          scrollRef.current = event.top;
        }}
        onModelUpdated={onModelUpdated}
        onSortChanged={onSortChanged}
      />
      {pagination && (
        <AgGridPaginationComponent
          gridData={gridData}
          gridState={gridState}
          isDataLoading={isDataLoading}
          updateGridState={updateGridState}
          isDisablePaginationActions={isDisablePaginationActions}
          gridRef={gridRef}
          scrollRef={scrollRef}
        />
      )}
    </div>
  );
};

export default ServerPaginationTable;
